import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import {
  Plus, SpinnerGap, Star, Trophy, Warning, X, UploadSimple, CheckCircle, CalendarBlank, Pencil, Trash,
} from "@phosphor-icons/react";

const EMPTY = {
  titulo: "", descricao: "", data_inicio: "", data_alvo: "", valor: "",
  premio: "", consequencia: "",
  foto_sonho_url: "", foto_premio_url: "",
};

function toFormShape(s) {
  return {
    ID_Sonho: s.ID_Sonho,
    titulo: s.Titulo || "",
    descricao: s.Descricao || "",
    data_inicio: s.DataInicio ? s.DataInicio.substring(0, 10) : "",
    data_alvo: s.DataAlvo ? s.DataAlvo.substring(0, 10) : "",
    valor: s.Valor ?? "",
    premio: s.Premio || "",
    consequencia: s.Consequencia || "",
    foto_sonho_url: s.FotoSonhoUrl || "",
    foto_premio_url: s.FotoPremioUrl || "",
  };
}

export default function DreamsSonhos() {
  const [sonhos, setSonhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // sonho object | "new" | null

  const refresh = async () => {
    const { data } = await api.get("/sonhos");
    setSonhos(data.data || []);
    setLoading(false);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/sonhos");
      if (!cancelled) {
        setSonhos(data.data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const remove = async (id) => {
    if (!confirm("Apagar este sonho?")) return;
    await api.delete(`/sonhos/${id}`);
    await refresh();
  };

  const complete = async (id) => {
    await api.post(`/sonhos/${id}/complete`);
    await refresh();
  };

  return (
    <div className="space-y-8 pb-12">
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#6F4F1D]/80">Sprint atual</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Os meus <span className="text-[#6F4F1D]">sonhos</span></h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-dream-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm hover:shadow-[0_0_30px_rgba(245,158,11,0.3)]">
          <Plus size={14} weight="bold" /> Novo sonho
        </button>
      </header>

      {loading ? (
        <p className="text-[#978D78] flex items-center gap-2"><SpinnerGap size={16} className="animate-spin" /> A carregar...</p>
      ) : sonhos.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E6E1D6] p-12 text-center">
          <Star size={28} weight="fill" className="text-[#6F4F1D]/60 mx-auto" />
          <p className="text-[#4B5563] mt-3">Ainda não tens sonhos. Cria o primeiro!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sonhos.map(s => (
            <article key={s.ID_Sonho} data-testid={`sonho-${s.ID_Sonho}`}
                     className={`relative rounded-2xl border bg-white/[0.02] overflow-hidden flex flex-col transition-all
                       ${s.Estado === "CO" ? "border-emerald-400/40 opacity-80" : "border-[#E6E1D6] hover:border-[#6F4F1D]/25"}`}>
              <div className="relative aspect-[5/3] bg-gradient-to-br from-violet-700/30 to-fuchsia-700/30">
                {s.FotoSonhoUrl ? (
                  <img src={s.FotoSonhoUrl} alt={s.Titulo} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#6F4F1D]/40">
                    <Star size={42} weight="fill" />
                  </div>
                )}
                {s.Estado === "CO" && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-emerald-400 text-[#0F1829] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle size={10} weight="fill" /> Concluído
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <Link to={`/dreams/sonhos/${s.ID_Sonho}`} data-testid={`open-${s.ID_Sonho}`}
                      className="hover:text-[#6F4F1D] transition-colors">
                  <h3 className="font-semibold text-base leading-snug">{s.Titulo}</h3>
                </Link>
                {s.Descricao && <p className="text-xs text-[#4B5563] mt-1.5 line-clamp-3">{s.Descricao}</p>}
                <div className="text-[11px] text-[#978D78] mt-3 space-y-1">
                  {s.DataAlvo && <p className="flex items-center gap-1.5"><CalendarBlank size={11} /> {new Date(s.DataAlvo).toLocaleDateString("pt-PT")}</p>}
                  {s.Valor != null && <p>💰 {Number(s.Valor).toLocaleString("pt-PT")}€</p>}
                  {s.Premio && <p className="flex items-start gap-1.5"><Trophy size={11} className="mt-0.5 shrink-0" /> {s.Premio}</p>}
                  {s.Consequencia && <p className="flex items-start gap-1.5 text-rose-300/80"><Warning size={11} className="mt-0.5 shrink-0" /> {s.Consequencia}</p>}
                </div>
                <div className="mt-auto pt-4 flex gap-1.5">
                  {s.Estado !== "CO" && (
                    <button onClick={() => complete(s.ID_Sonho)} data-testid={`complete-${s.ID_Sonho}`}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-full bg-emerald-400/20 border border-emerald-400/40 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-400/30">
                      <CheckCircle size={11} weight="fill" /> Concluir
                    </button>
                  )}
                  <button onClick={() => setEditing(toFormShape(s))} data-testid={`edit-${s.ID_Sonho}`}
                          className="px-2.5 py-1.5 rounded-full bg-white border border-[#E6E1D6] text-[#4B5563] hover:bg-white"><Pencil size={11} /></button>
                  <button onClick={() => remove(s.ID_Sonho)} data-testid={`del-${s.ID_Sonho}`}
                          className="px-2.5 py-1.5 rounded-full bg-white border border-[#E6E1D6] text-rose-300 hover:bg-rose-500/20"><Trash size={11} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <SonhoModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function SonhoModal({ initial, onClose, onSaved }) {
  const isNew = !initial.ID_Sonho;
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const uploadImage = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, [field]: data.url }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha no upload");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (isNew) await api.post("/sonhos", form);
      else       await api.patch(`/sonhos/${initial.ID_Sonho}`, form);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="sonho-modal"
           className="bg-[#F5F2EB] border border-[#E6E1D6] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="sticky top-0 bg-[#F5F2EB] border-b border-[#E6E1D6] px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isNew ? "Novo sonho" : "Editar sonho"}</h2>
          <button onClick={onClose} className="text-[#978D78] hover:text-white"><X size={20} /></button>
        </header>
        <div className="p-6 space-y-4">
          <Field label="Título *"><input data-testid="f-titulo" value={form.titulo} onChange={setF("titulo")} className={inputCls} placeholder="Ex.: Comprar casa nas montanhas" /></Field>
          <Field label="Descrição"><textarea data-testid="f-descricao" value={form.descricao} onChange={setF("descricao")} rows={3} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Data de início"><input data-testid="f-data-inicio" type="date" value={form.data_inicio} onChange={setF("data_inicio")} className={inputCls} /></Field>
            <Field label="Data alvo"><input data-testid="f-data" type="date" value={form.data_alvo} onChange={setF("data_alvo")} className={inputCls} /></Field>
            <Field label="Valor (€)"><input data-testid="f-valor" type="number" min={0} step="0.01" value={form.valor} onChange={setF("valor")} className={inputCls} /></Field>
          </div>
          <Field label="Prémio que te dás ao alcançar"><input data-testid="f-premio" value={form.premio} onChange={setF("premio")} className={inputCls} placeholder="Ex.: Viagem ao Japão" /></Field>
          <Field label="Consequência se não alcançares"><input data-testid="f-conseq" value={form.consequencia} onChange={setF("consequencia")} className={inputCls} placeholder="Ex.: Doar 200€ a uma instituição" /></Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <ImageField label="Foto do sonho" url={form.foto_sonho_url} onUpload={uploadImage("foto_sonho_url")} onClear={() => setForm(f => ({ ...f, foto_sonho_url: "" }))} testid="img-sonho" />
            <ImageField label="Foto do prémio" url={form.foto_premio_url} onUpload={uploadImage("foto_premio_url")} onClear={() => setForm(f => ({ ...f, foto_premio_url: "" }))} testid="img-premio" />
          </div>

          {err && <p className="text-xs text-red-300" data-testid="modal-err">{err}</p>}
        </div>
        <footer className="sticky bottom-0 bg-[#F5F2EB] border-t border-[#E6E1D6] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#4B5563] hover:bg-white">Cancelar</button>
          <button onClick={submit} disabled={busy || !form.titulo.trim()} data-testid="save-sonho"
                  className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#6F4F1D] text-[#0F1829] text-sm font-semibold disabled:opacity-40">
            {busy ? <SpinnerGap size={14} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />}
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#6B7280] focus:border-amber-400/50 outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-[#978D78] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ImageField({ label, url, onUpload, onClear, testid }) {
  return (
    <Field label={label}>
      <div className="relative aspect-video rounded-lg overflow-hidden border border-[#E6E1D6] bg-white">
        {url ? (
          <>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button onClick={onClear} type="button" data-testid={`${testid}-clear`}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white">
              <X size={12} />
            </button>
          </>
        ) : (
          <label data-testid={testid} className="absolute inset-0 flex flex-col items-center justify-center text-[#978D78] cursor-pointer hover:bg-white">
            <UploadSimple size={20} />
            <span className="text-[11px] mt-1">Carregar imagem</span>
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
          </label>
        )}
      </div>
    </Field>
  );
}
